import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen, LogOut, ShieldCheck, Settings } from "lucide-react";
import { LogoMark } from "@/components/AuthShell";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const Count = ({ n, warn, mini }) => (n > 0 || (!mini && n === 0)) && (mini
  ? <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full text-[9px] font-bold flex items-center justify-center px-1 text-white ${warn ? "bg-red-500" : "bg-sky-500"}`}>{n}</span>
  : <span className={`text-[11px] rounded-full px-2 ${warn && n > 0 ? "bg-red-500/80 text-white" : "bg-white/10"}`}>{n}</span>);

export const itemCls = (active, mini) => `relative mx-2 my-0.5 rounded-md flex items-center gap-2.5 text-[13px] cursor-pointer select-none transition-colors ${mini ? "justify-center h-9 w-9 mx-auto" : "px-3 py-1.5"} ${active ? (mini ? "bg-sky-500/20 text-sky-300" : "bg-sky-500/20 text-white font-semibold") : "text-white/70 hover:bg-white/5"}`;

// Fold toggle shown on the first row of a section (like the chosen model); remembered per browser.
export const useFold = (id) => {
  const [folded, setFolded] = useState(localStorage.getItem(`sv2-fold-${id}`) === "1");
  const toggle = (e) => { e.preventDefault(); e.stopPropagation(); localStorage.setItem(`sv2-fold-${id}`, folded ? "0" : "1"); setFolded(!folded); };
  const btn = (
    <button onClick={toggle} title={folded ? "Déplier" : "Replier"} data-testid={`sv2-fold-${id}`} className="h-5 w-5 rounded hover:bg-white/10 flex items-center justify-center opacity-60 hover:opacity-100">
      {folded ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
    </button>
  );
  return [folded, btn];
};

export const Section2 = ({ id, title, mini, extra, children }) => (
  <div className="mt-3" data-testid={`sv2-section-${id}`}>
    {mini ? <div className="mx-3 mb-1 border-t border-white/10" /> : (
      <p className="px-4 pb-1 flex items-center text-[10px] uppercase tracking-[0.14em] text-white/40">{title}<span className="ml-auto">{extra}</span></p>
    )}
    {children}
  </div>
);

const NavItem = ({ to, end, label, icon: Icon, id, badge, mini, fold }) => (
  <NavLink to={to} end={end} data-testid={id} title={label} className={({ isActive }) => itemCls(isActive, mini)}>
    {({ isActive }) => <>
      <Icon className={mini ? "h-[18px] w-[18px]" : `h-4 w-4 ${isActive ? "text-sky-300" : "opacity-60"}`} />
      {!mini && <span className="flex-1 truncate">{label}</span>}
      {!mini && fold}
      {badge > 0 && <Count n={badge} warn mini={mini} />}
    </>}
  </NavLink>
);

const Spaces = ({ items, mini, accessBadge }) => {
  const [folded, fold] = useFold("spaces");
  return (
    <Section2 id="spaces" title="Espaces" mini={mini}>
      {items.filter((_, i) => i === 0 || !folded || mini).map((n, i) => <NavItem key={n.to} {...n} mini={mini} fold={i === 0 && fold} badge={n.id === "rail-access" ? accessBadge : 0} />)}
    </Section2>
  );
};

// "Admin" group at the bottom: Paramètres, Équipe, Abonnement (+ Plateforme), folded by default.
const AdminGroup = ({ items, mini }) => {
  const { pathname } = useLocation();
  const inside = items.some((n) => pathname.startsWith(n.to));
  const [open, setOpen] = useState(localStorage.getItem("sv2-admin-open") === "1" || inside);
  useEffect(() => { if (inside) setOpen(true); }, [inside]);
  const toggle = () => { localStorage.setItem("sv2-admin-open", open ? "0" : "1"); setOpen(!open); };
  const show = open || mini;
  return (
    <>
      {!mini && (
        <button onClick={toggle} data-testid="sv2-admin-toggle" className={`${itemCls(inside && !show, false)} w-[calc(100%-1rem)]`}>
          <Settings className="h-4 w-4 opacity-70" /><span className="flex-1 text-left">Admin</span>
          {show ? <ChevronDown className="h-3.5 w-3.5 opacity-60" /> : <ChevronRight className="h-3.5 w-3.5 opacity-60" />}
        </button>
      )}
      {show && <div className={mini ? "" : "pl-4"}>{items.map((n) => <NavItem key={n.to} {...n} mini={mini} />)}</div>}
    </>
  );
};

export const Sidebar2 = ({ top, bottom, user, admin, mini, setMini, setSlot, onLogout, accessBadge }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isAccess = pathname === "/app" || pathname === "/app/";
  const spaces = [...top.filter((n) => n.id === "rail-access"), ...top.filter((n) => n.id !== "rail-access"), ...bottom.filter((n) => n.id === "rail-deploy")];
  const more = bottom.filter((n) => !["rail-deploy", "rail-admin"].includes(n.id));
  const initials = (user?.name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const adminItems = [
    { to: "/app/admin", label: "Paramètres", icon: Settings, id: "rail-admin" },
    ...bottom.filter((n) => ["rail-team", "rail-billing", "rail-superadmin"].includes(n.id)),
  ];
  return (
    <aside className={`flex-shrink-0 bg-[#0b1e3f] text-white flex flex-col min-h-0 overflow-hidden transition-[width] duration-200 ${mini ? "w-16" : "w-[300px]"}`} data-testid="sv2-sidebar">
      <div className={`pt-4 pb-3 flex items-center gap-2.5 ${mini ? "justify-center" : "px-4"}`}>
        <LogoMark className={mini ? "h-9 w-9 shrink-0" : "h-10 w-10 shrink-0"} />
        {!mini && <span className="text-xl font-extrabold flex-1 truncate" title={user?.organization?.name}>ScreenView</span>}
        {!mini && <button onClick={() => setMini(true)} title="Réduire le menu" data-testid="sv2-collapse-btn" className="h-7 w-7 rounded-md hover:bg-white/10 flex items-center justify-center"><PanelLeftClose className="h-4 w-4" /></button>}
      </div>
      {mini && <button onClick={() => setMini(false)} title="Agrandir le menu" data-testid="sv2-expand-btn" className="mx-auto h-9 w-9 rounded-md hover:bg-white/10 flex items-center justify-center text-white/70"><PanelLeftOpen className="h-[18px] w-[18px]" /></button>}
      <div className="sv2-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-2">
        <Spaces items={spaces} mini={mini} accessBadge={accessBadge} />
        {isAccess && <div ref={setSlot} data-testid="sv2-access-slot" />}
      </div>
      <div className="border-t border-white/10 pt-2" data-testid="sv2-admin-row">
        {admin && <AdminGroup items={adminItems} mini={mini} />}
        <div className={`flex items-center gap-1 pb-2 ${admin ? "pt-1" : ""}`}>
        {!mini && <span className="flex-1 px-5 text-[13px] text-white/80 truncate">{user?.name}</span>}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-btn" title={user?.name} className={`h-7 w-7 rounded-full bg-sky-500 text-[10px] font-bold flex items-center justify-center shrink-0 hover:ring-2 hover:ring-sky-300 ${mini ? "mx-auto" : "mr-4"}`}>{initials}</button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end" className="w-60">
            <DropdownMenuLabel>
              <p data-testid="current-user-name">{user?.name}</p>
              <p className="text-xs font-normal text-muted-foreground" data-testid="org-name">{user?.organization?.name} · {admin ? "Administrateur" : "Technicien"}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {!admin && more.map((n) => <DropdownMenuItem key={n.to} onClick={() => navigate(n.to)} data-testid={n.id}><n.icon className="h-4 w-4 mr-2" /> {n.label}</DropdownMenuItem>)}
            <DropdownMenuItem onClick={() => navigate("/app/security")} data-testid="security-menu-item"><ShieldCheck className="h-4 w-4 mr-2" /> Sécurité (2FA)</DropdownMenuItem>
            <DropdownMenuItem onClick={onLogout} data-testid="logout-btn"><LogOut className="h-4 w-4 mr-2" /> Déconnexion</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </div>
    </aside>
  );
};
